import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";

import { logError } from "utility/Function";
import { platformGet, PlatformRestError } from "./platform";

export const DASHBOARD_TILES_PATH = "/api/v1/dashboard/tiles/";

/**
 * A tile as the snapshot stores it. A suppressed tile carries no figure and
 * no `n` — "not enough data yet", which is not the same answer as zero.
 */
export type Tile<T> =
  | { suppressed: true; value: null }
  | { suppressed: false; value: T; n: number };

export type TopicSplit = {
  topics: { topic: string; completions: number }[];
  untagged: number;
  without_metadata: number;
};

export type PopularItem = {
  content_id: string;
  title: string;
  minutes: number;
  people: number;
};

export type EngagementTiles = {
  employees_in_scope: Tile<number>;
  activated: Tile<number>;
  ever_engaged: Tile<number>;
  repeat_engaged: Tile<number>;
  content_duration_minutes: Tile<{ minutes: number; engagements: number }>;
  topic_split: Tile<TopicSplit>;
  popular_content: Tile<PopularItem[]>;
};

type TilesResponse = { company_name: string } & (
  | { status: "computing"; computed_at: null; tiles: null }
  | { status: "ready"; computed_at: string; tiles: EngagementTiles }
);

// `companyName` is the company the figures are about, as Platform resolved
// it — the one this admin administers, which is not always their employer.
export type DashboardTiles =
  | { state: "loading" }
  | { state: "computing"; companyName: string }
  | { state: "error" }
  | {
      state: "ready";
      companyName: string;
      computedAt: Date;
      tiles: EngagementTiles;
    };

// A snapshot is computed in minutes, so a company switched on moments ago
// sees its figures without reloading.
const POLL_WHILE_COMPUTING_MS = 30_000;

// Same reasoning as the access check: one 502 during a deploy roll should
// not read as a broken dashboard.
const RETRY_DELAYS_MS = [500, 1500];

// An expired session, or web running ahead of Platform.
const UNREMARKABLE_STATUSES = [401, 404];

const TilesContext = createContext<DashboardTiles>({ state: "loading" });

/**
 * The company's stored dashboard snapshot, fetched once for every tab.
 *
 * The figures are precomputed nightly, so nothing here waits on a query —
 * the only wait is a company whose first snapshot has not been stored yet,
 * which answers "computing" and is asked again until it is ready.
 */
export function DashboardTilesProvider({
  children,
  pollMs = POLL_WHILE_COMPUTING_MS,
  retryDelaysMs = RETRY_DELAYS_MS,
}: {
  children: ReactNode;
  // Overridden only by tests, so polling and retries run without the wait.
  pollMs?: number;
  retryDelaysMs?: number[];
}) {
  const [tiles, setTiles] = useState<DashboardTiles>({ state: "loading" });
  const timingRef = useRef({ pollMs, retryDelaysMs });
  timingRef.current = { pollMs, retryDelaysMs };

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (failuresSoFar: number): void => {
      platformGet<TilesResponse>(DASHBOARD_TILES_PATH)
        .then((response) => {
          if (cancelled) return;
          if (response.status === "ready") {
            setTiles({
              state: "ready",
              companyName: response.company_name,
              computedAt: new Date(response.computed_at),
              tiles: response.tiles,
            });
            return;
          }
          setTiles({ state: "computing", companyName: response.company_name });
          timer = setTimeout(() => attempt(0), timingRef.current.pollMs);
        })
        .catch((error) => {
          if (cancelled) return;
          const delays = timingRef.current.retryDelaysMs;
          if (failuresSoFar < delays.length) {
            timer = setTimeout(
              () => attempt(failuresSoFar + 1),
              delays[failuresSoFar]
            );
            return;
          }
          const status =
            error instanceof PlatformRestError ? error.status : undefined;
          if (status === undefined || !UNREMARKABLE_STATUSES.includes(status)) {
            logError("Company dashboard tiles failed to load", error);
          }
          setTiles({ state: "error" });
        });
    };

    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <TilesContext.Provider value={tiles}>{children}</TilesContext.Provider>
  );
}

export function useDashboardTiles(): DashboardTiles {
  return useContext(TilesContext);
}
